import type { PageParticipant } from "../../../../shared/contracts/product-pages.js";
import { buildImportSetup, type ReviewedImportInput } from "./ggb-round.js";
import {
  cardGross,
  guestAssignment,
  validateReview,
  type ReviewState,
} from "./ggb-review.js";
import { importValue } from "./ggb-import-value.js";
import { buildServerImportRequest } from "./ggb-server-request.js";
import type {
  GameBookImportRequest,
  GameBookImportReceipt,
} from "../../../../shared/contracts/gamebook-import.js";

interface Storage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}
interface Target {
  id: string;
  displayName: string;
}
export interface CreatedGuest extends Target {
  gross: number | null;
}
export interface ImportIntent {
  storageVersion: 1 | 2;
  accountId: string;
  roundId: string;
  review: ReviewState;
  resolved: Record<string, Target>;
  createdGuests: CreatedGuest[];
  uncertainGuest: string | null;
  prepared: ReviewedImportInput | null;
  completed: boolean;
  serverRequest?: GameBookImportRequest;
}
const storageKey = (accountId: string) =>
  `fairway-rivalry:gamebook-import:${accountId}`;

/** A device-local retry receipt; it is deliberately not server deduplication. */
export class GameBookImportSession {
  public constructor(
    private readonly storage: Storage,
    private readonly accountId: string,
  ) {}

  public read(): ImportIntent | null {
    const raw = this.storage.getItem(storageKey(this.accountId));
    if (!raw) return null;
    const value = JSON.parse(raw) as ImportIntent;
    if (
      ![1, 2].includes(value.storageVersion) ||
      value.accountId !== this.accountId ||
      typeof value.roundId !== "string" ||
      !Array.isArray(value.review?.cards) ||
      !value.resolved ||
      !Array.isArray(value.createdGuests)
    )
      throw new Error(
        "The saved import cannot be read. Its retry receipt has been kept.",
      );
    if (
      value.storageVersion === 2 &&
      (!value.serverRequest ||
        (value.serverRequest.requestId !== value.roundId && !value.completed))
    )
      throw new Error(
        "The saved server import receipt is incomplete. It was preserved for recovery.",
      );
    return value;
  }

  public beginServer(review: ReviewState): ImportIntent {
    const prior = this.read();
    if (prior && !prior.completed) {
      if (importValue(prior.review) !== importValue(review))
        throw new Error("Continue the saved import with its original review.");
      return prior;
    }
    const request = buildServerImportRequest(review, crypto.randomUUID());
    const next: ImportIntent = {
      storageVersion: 2,
      accountId: this.accountId,
      roundId: request.requestId,
      review: structuredClone(review),
      resolved: {},
      createdGuests: [],
      uncertainGuest: null,
      prepared: null,
      completed: false,
      serverRequest: request,
    };
    this.write(next);
    return next;
  }

  public completeServer(receipt: GameBookImportReceipt): ImportIntent {
    const intent = this.requireIntent();
    if (intent.storageVersion !== 2 || !intent.serverRequest)
      throw new Error("This is not a server import receipt.");
    intent.roundId = receipt.roundId;
    intent.createdGuests = receipt.createdGuests;
    intent.completed = true;
    this.write(intent);
    return intent;
  }

  public rejectServer(requestId: string): void {
    const intent = this.read();
    if (
      intent?.storageVersion === 2 &&
      !intent.completed &&
      intent.serverRequest?.requestId === requestId
    )
      this.storage.removeItem(storageKey(this.accountId));
  }

  public begin(review: ReviewState): ImportIntent {
    const problem = validateReview(review, undefined);
    if (problem) throw new Error(problem);
    const prior = this.read();
    if (prior && importValue(prior.review) === importValue(review))
      return prior;
    if (prior && !prior.completed)
      throw new Error(
        "Continue the saved import with its original review before importing another round.",
      );
    const next: ImportIntent = {
      storageVersion: 1,
      accountId: this.accountId,
      roundId: crypto.randomUUID(),
      review: structuredClone(review),
      resolved: {},
      createdGuests: [],
      uncertainGuest: null,
      prepared: null,
      completed: false,
    };
    this.write(next);
    return next;
  }

  public recoverGuest(target: Target): void {
    const intent = this.requireIntent();
    if (intent.storageVersion === 2)
      throw new Error(
        "Guests in this import are resolved atomically by the server.",
      );
    if (
      !intent.uncertainGuest ||
      intent.prepared ||
      Object.values(intent.resolved).some((value) => value.id === target.id)
    )
      throw new Error(
        "Choose a different existing player for the uncertain guest.",
      );
    const card = intent.review.cards.find(
      (value) => value.key === intent.uncertainGuest,
    );
    if (!card || !target.id || !target.displayName.trim())
      throw new Error("The guest recovery is incomplete.");
    intent.resolved[intent.uncertainGuest] = target;
    intent.uncertainGuest = null;
    this.write(intent);
  }

  public async prepare(
    participants: readonly PageParticipant[],
    createGuest: (displayName: string) => Promise<Target>,
    access: () => void,
  ): Promise<ImportIntent> {
    const intent = this.requireIntent();
    if (intent.prepared) return intent;
    for (const card of intent.review.cards) {
      access();
      if (!card.assign || intent.resolved[card.key]) continue;
      if (card.assign === guestAssignment) {
        if (intent.uncertainGuest)
          throw new Error(
            "The guest request was not confirmed. Refresh the player list and select that existing guest below; a second guest will not be created automatically.",
          );
        intent.uncertainGuest = card.key;
        this.write(intent);
        const target = await createGuest(card.name.trim().slice(0, 80));
        access();
        intent.resolved[card.key] = target;
        intent.createdGuests.push({
          ...target,
          gross: cardGross(card, intent.review.holes),
        });
        intent.uncertainGuest = null;
        this.write(intent);
      } else {
        const target = participants.find((player) => player.id === card.assign);
        if (!target)
          throw new Error(
            `${card.name}: the chosen player is no longer available.`,
          );
        intent.resolved[card.key] = {
          id: target.id,
          displayName: target.displayName,
        };
        this.write(intent);
      }
    }
    access();
    intent.prepared = buildImportSetup(
      intent.review,
      new Map(Object.entries(intent.resolved)),
      intent.roundId,
    );
    this.write(intent);
    return intent;
  }

  public complete(): ImportIntent {
    const intent = this.requireIntent();
    if (!intent.prepared) throw new Error("The import was not prepared.");
    intent.completed = true;
    this.write(intent);
    return intent;
  }

  private requireIntent(): ImportIntent {
    const intent = this.read();
    if (!intent) throw new Error("The saved import is missing.");
    return intent;
  }

  private write(intent: ImportIntent): void {
    this.storage.setItem(storageKey(this.accountId), JSON.stringify(intent));
  }
}

export function clearGameBookImportIntent(
  accountId: string,
  storage: Storage,
): void {
  storage.removeItem(storageKey(accountId));
}
